
const FLOATING_TOOLTIP_CLASS = 'se-floating-tooltip';

const ensureFloatingTooltipStyles = (() => {
  let injected = false;
  return () => {
    if (injected) return;
    injected = true;
    const style = document.createElement('style');
    style.textContent = `
      .${FLOATING_TOOLTIP_CLASS} {
        position: fixed;
        z-index: 2147483647;
        background: rgb(255, 255, 255);
        color: rgb(50, 49, 48);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        line-height: 1.4;
        white-space: normal;
        word-break: break-word;
        overflow-wrap: anywhere;
        max-width: 360px;
        width: max-content;
        pointer-events: none;
        font-family: "ABBvoice Lt";
        box-shadow: 0 4px 12px rgba(0,0,0,0.25);
        opacity: 0;
        transition: opacity 0.15s;
      }
      .${FLOATING_TOOLTIP_CLASS}.visible {
        opacity: 1;
      }
      .${FLOATING_TOOLTIP_CLASS} ul,
      .${FLOATING_TOOLTIP_CLASS} ol {
        margin: 0;
        padding-left: 20px;
      }
      .${FLOATING_TOOLTIP_CLASS} p {
        margin: 0;
      }
    `;
    document.head.appendChild(style);
  };
})();

let floatingTooltipEl: HTMLDivElement | null = null;
let activeTooltipSource: HTMLElement | null = null;

function positionFloatingTooltip() {
  if (!floatingTooltipEl || !activeTooltipSource) return;

  const rect = activeTooltipSource.getBoundingClientRect();
  const tooltipRect = floatingTooltipEl.getBoundingClientRect();

  let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
  left = Math.max(8, Math.min(left, window.innerWidth - tooltipRect.width - 8));

  let top = rect.top - tooltipRect.height - 8;
  if (top < 4) {
    top = rect.bottom + 8;
  }

  floatingTooltipEl.style.left = `${left}px`;
  floatingTooltipEl.style.top = `${top}px`;
}

const ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'BR',
  'P',
  'UL',
  'OL',
  'LI',
  'SPAN',
  'DIV'
]);

const ALLOWED_STYLE_PROPERTIES = new Set([
  'color',
  'background-color',
  'font-weight',
  'font-style',
  'text-decoration'
]);

function showFloatingTooltip(source: HTMLElement) {
  const tip = source.getAttribute('data-tip')?.trim();
  if (!tip) return;

  ensureFloatingTooltipStyles();

  if (!floatingTooltipEl) {
    floatingTooltipEl = document.createElement('div');
    floatingTooltipEl.className = FLOATING_TOOLTIP_CLASS;
    document.body.appendChild(floatingTooltipEl);
  }

  floatingTooltipEl.innerHTML = sanitizeTooltipHtml(tip);
  activeTooltipSource = source;
  positionFloatingTooltip();

  requestAnimationFrame(() => {
    floatingTooltipEl?.classList.add('visible');
  });
}

function sanitizeInlineStyle(styleValue: string): string {
  return styleValue
    .split(';')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => {
      const separatorIndex = part.indexOf(':');
      if (separatorIndex < 0) return '';

      const property = part.slice(0, separatorIndex).trim().toLowerCase();
      const value = part.slice(separatorIndex + 1).trim();

      if (!ALLOWED_STYLE_PROPERTIES.has(property)) return '';
      if (/url\s*\(/i.test(value)) return '';

      return `${property}: ${value}`;
    })
    .filter(Boolean)
    .join('; ');
}

function sanitizeTooltipHtml(rawHtml: string): string {
  const template = document.createElement('template');
  template.innerHTML = rawHtml;

  const sanitizeNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) return;

    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.parentNode?.removeChild(node);
      return;
    }

    const element = node as HTMLElement;

    if (!ALLOWED_TAGS.has(element.tagName)) {
      const parent = element.parentNode;
      if (!parent) return;

      while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
      }
      parent.removeChild(element);
      return;
    }

    for (const attr of Array.from(element.attributes)) {
      const name = attr.name.toLowerCase();

      if (name.startsWith('on')) {
        element.removeAttribute(attr.name);
        continue;
      }

      if (name === 'style') {
        const sanitizedStyle = sanitizeInlineStyle(attr.value);
        if (sanitizedStyle) {
          element.setAttribute('style', sanitizedStyle);
        } else {
          element.removeAttribute('style');
        }
        continue;
      }

      if (name !== 'class') {
        element.removeAttribute(attr.name);
      }
    }

    Array.from(element.childNodes).forEach(sanitizeNode);
  };

  Array.from(template.content.childNodes).forEach(sanitizeNode);
  return template.innerHTML.trim();
}

export function hideFloatingTooltip() {
  if (floatingTooltipEl) {
    floatingTooltipEl.classList.remove('visible');
  }
  activeTooltipSource = null;
}

function handleDocumentMouseOver(e: MouseEvent) {
  const target = e.target instanceof HTMLElement ? e.target : null;
  const tooltipEl = target?.closest('.se-tooltip[data-tip]') as HTMLElement | null;

  if (tooltipEl && tooltipEl !== activeTooltipSource) {
    showFloatingTooltip(tooltipEl);
  }
}

function handleDocumentMouseOut(e: MouseEvent) {
  if (!activeTooltipSource) return;

  const related = e.relatedTarget instanceof HTMLElement ? e.relatedTarget : null;
  if (related && activeTooltipSource.contains(related)) return;

  const fromTooltip =
    e.target instanceof HTMLElement &&
    e.target.closest('.se-tooltip[data-tip]') === activeTooltipSource;
  if (fromTooltip) {
    hideFloatingTooltip();
  }
}

document.addEventListener('mouseover', handleDocumentMouseOver, true);
document.addEventListener('mouseout', handleDocumentMouseOut, true);
